import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaCog, FaSpinner } from 'react-icons/fa';
import RichTextEditor from './RichTextEditor';

const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}`;

const ToggleSwitch = ({ enabled, onChange, disabled }) => (
    <button
        type="button"
        onClick={onChange}
        disabled={disabled}
        className={`${enabled ? 'bg-blue-600' : 'bg-gray-200'}
            relative inline-flex items-center h-6 rounded-full w-11 transition-colors
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
            disabled:opacity-50 disabled:cursor-not-allowed`}
    >
        <span
            className={`${enabled ? 'translate-x-6' : 'translate-x-1'}
                inline-block w-4 h-4 transform bg-white rounded-full transition-transform`}
        />
    </button>
);

const DEFAULT_CHAT_SETTINGS = {
    l1IntroText: '',
    showSummaryBelowTitle: false,
    allowLessonHyperlinks: true,
    l1LessonCount: 3,
    l2LessonCount: 3,
    l3LessonCount: 3,
    l2Explanation: '',
    l3Explanation: '',
    finalChatText: '',
    bonusLessonsHeaderText: '',
    pdfDownloadMessage: '',
    finalRedirectUrl: '',
    enablePdfDownload: true,
    lockLightMode: false,
};

const DEBOUNCE_DELAY = 600;

const ChatSettingsCard = () => {
    const [chatSettings, setChatSettings] = useState(DEFAULT_CHAT_SETTINGS);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const updateTimers = useRef({});

    const fetchChatSettings = useCallback(async () => {
        setIsLoading(true);
        setError('');
        try {
            const response = await axios.post(`${API_BASE_URL}/api/v1/admin/get-settings`);
            const data = response.data?.data || {};
            setChatSettings(prev => ({
                ...prev,
                ...data,
            }));
        } catch (err) {
            console.error("Failed to fetch chat settings:", err);
            setError(err.response?.data?.message || 'Could not load chat settings. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchChatSettings();
    }, [fetchChatSettings]);

    const scheduleSettingUpdate = useCallback((field, value) => {
        if (updateTimers.current[field]) {
            clearTimeout(updateTimers.current[field]);
        }

        updateTimers.current[field] = setTimeout(async () => {
            try {
                await axios.post(`${API_BASE_URL}/api/v1/admin/update-settings`, null, {
                    params: {
                        key: field,
                        value: String(value ?? ''),
                    }
                });
            } catch (err) {
                console.error(`Failed to update ${field}:`, err);
                toast.error(`Failed to update ${field}.`);
            }
        }, DEBOUNCE_DELAY);
    }, []);

    useEffect(() => {
        return () => {
            Object.values(updateTimers.current).forEach(clearTimeout);
        };
    }, []);

    const handleChatSettingsChange = (field, value) => {
        setChatSettings(prev => ({
            ...prev,
            [field]: value,
        }));
        scheduleSettingUpdate(field, value);
    };

    const handleChatSettingsToggle = (field) => {
        setChatSettings(prev => {
            const updatedValue = !prev[field];
            scheduleSettingUpdate(field, updatedValue);
            return {
                ...prev,
                [field]: updatedValue,
            };
        });
    };


    return (
        <div className="mt-8 bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                        <FaCog className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Chat Settings
                        </h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            Fine-tune how lessons and summaries show up in the chat flow.
                        </p>
                    </div>
                </div>
            </div>

            <div className="p-6 space-y-6">
                {isLoading ? (
                    <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
                        <FaSpinner className="animate-spin h-4 w-4 text-purple-600" />
                        Loading chat settings...
                    </div>
                ) : (
                    <>
                        {error && (
                            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-200">
                                {error}
                            </div>
                        )}

                        {/* Global Theme Setting - Applies to all Rich Text Editor content */}
                        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                            <div className="flex items-center justify-between">
                                <div className="flex-1">
                                    <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                                        Force Light Mode Only (User Panel)
                                    </p>
                                    <p className="text-xs text-gray-600 dark:text-gray-400">
                                        When enabled, the <strong>user panel</strong> will always display in Light Mode only. This setting does not affect the admin panel. It ensures all rich text content in the user panel (Level 1 Intro Text, L2/L3 Explanations, Bonus Lessons, Final Chat Text, and Questions) maintains consistent visibility and appearance without being affected by theme switching.
                                    </p>
                                </div>
                                <div className="ml-4">
                                    <ToggleSwitch
                                        enabled={chatSettings.lockLightMode}
                                        onChange={() => handleChatSettingsToggle('lockLightMode')}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="col-span-1 lg:col-span-2">
                                <label htmlFor="l1IntroText" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Level 1 Intro Text (First text after question responses)
                                </label>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                    This text appears right after the user completes the questions, before showing the lesson recommendations. You can format text with <strong>bold</strong>, <em>italics</em>, colors, and more.
                                </p>
                                <RichTextEditor
                                    id="l1IntroText"
                                    value={chatSettings.l1IntroText}
                                    onChange={(html) => handleChatSettingsChange('l1IntroText', html)}
                                    placeholder="Great! Based on your answers, here are some top recommendations for you:"
                                />
                            </div>

                            <div className="flex items-center justify-between p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                                <div>
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">Show Summary Below Title</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Display lesson summaries directly below each lesson title.</p>
                                </div>
                                <ToggleSwitch
                                    enabled={chatSettings.showSummaryBelowTitle}
                                    onChange={() => handleChatSettingsToggle('showSummaryBelowTitle')}
                                />
                            </div>

                            <div className="flex items-center justify-between p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                                <div>
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">Allow Lesson Hyperlinks</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Make lesson titles clickable links to their detail pages.</p>
                                </div>
                                <ToggleSwitch
                                    enabled={chatSettings.allowLessonHyperlinks}
                                    onChange={() => handleChatSettingsToggle('allowLessonHyperlinks')}
                                />
                            </div>

                            <div>
                                <label htmlFor="l1LessonCount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Level 1 Lesson Count
                                </label>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                    Maximum number of Level 1 lessons to display. Leave empty or set to 0 to show all.
                                </p>
                                <input
                                    id="l1LessonCount"
                                    type="number"
                                    min="0"
                                    className="w-full rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-transparent text-gray-900 dark:text-white px-4 py-2.5 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    value={chatSettings.l1LessonCount}
                                    onChange={(e) => handleChatSettingsChange('l1LessonCount', Number(e.target.value))}
                                />
                            </div>

                            <div>
                                <label htmlFor="l2LessonCount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Level 2 Lesson Count
                                </label>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                    Maximum number of Level 2 lessons to display. Leave empty or set to 0 to show all.
                                </p>
                                <input
                                    id="l2LessonCount"
                                    type="number"
                                    min="0"
                                    className="w-full rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-transparent text-gray-900 dark:text-white px-4 py-2.5 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    value={chatSettings.l2LessonCount}
                                    onChange={(e) => handleChatSettingsChange('l2LessonCount', Number(e.target.value))}
                                />
                            </div>

                            <div>
                                <label htmlFor="l3LessonCount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Level 3 Lesson Count
                                </label>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                    Maximum number of Level 3 lessons to display. Leave empty or set to 0 to show all.
                                </p>
                                <input
                                    id="l3LessonCount"
                                    type="number"
                                    min="0"
                                    className="w-full rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-transparent text-gray-900 dark:text-white px-4 py-2.5 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    value={chatSettings.l3LessonCount}
                                    onChange={(e) => handleChatSettingsChange('l3LessonCount', Number(e.target.value))}
                                />
                            </div>

                            <div className="col-span-1 lg:col-span-2">
                                <label htmlFor="l2Explanation" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    L2 Explanation (Text after L1 lessons to explain L2 logic)
                                </label>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                    This text appears after Level 1 lessons and before Level 2 lessons to explain the progression. You can format text with <strong>bold</strong>, <em>italics</em>, colors, and more.
                                </p>
                                <RichTextEditor
                                    id="l2Explanation"
                                    value={chatSettings.l2Explanation}
                                    onChange={(html) => handleChatSettingsChange('l2Explanation', html)}
                                    placeholder="Explain how Level 2 builds from Level 1 lessons..."
                                />
                            </div>

                            <div className="col-span-1 lg:col-span-2">
                                <label htmlFor="l3Explanation" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    L3 Explanation (Text after L2 lessons to explain L3 logic)
                                </label>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                    This text appears after Level 2 lessons and before Level 3 lessons to explain the progression. You can format text with <strong>bold</strong>, <em>italics</em>, colors, and more.
                                </p>
                                <RichTextEditor
                                    id="l3Explanation"
                                    value={chatSettings.l3Explanation}
                                    onChange={(html) => handleChatSettingsChange('l3Explanation', html)}
                                    placeholder="Explain how Level 3 completes the learning path..."
                                />
                            </div>

                            <div className="col-span-1 lg:col-span-2">
                                <label htmlFor="bonusLessonsHeaderText" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Bonus Lessons Header Text
                                </label>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                    This text appears before bonus lessons to explain the special offer and access options. You can format text with <strong>bold</strong>, <em>italics</em>, colors, and more.
                                </p>
                                <RichTextEditor
                                    id="bonusLessonsHeaderText"
                                    value={chatSettings.bonusLessonsHeaderText}
                                    onChange={(html) => handleChatSettingsChange('bonusLessonsHeaderText', html)}
                                    placeholder="If you took the special offer for the extra 12 lessons, here are some bonus options for you. If you didn't, and you've changed your mind, you can still get access below."
                                />
                            </div>

                            <div className="col-span-1 lg:col-span-2">
                                <label htmlFor="finalChatText" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Final Chat Text
                                </label>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                    This text appears at the end of the conversation, after all lessons are displayed. Use it to wrap up and guide users to the next step. You can format text with <strong>bold</strong>, <em>italics</em>, colors, and more.
                                </p>
                                <RichTextEditor
                                    id="finalChatText"
                                    value={chatSettings.finalChatText}
                                    onChange={(html) => handleChatSettingsChange('finalChatText', html)}
                                    placeholder="Wrap up the conversation and position the redirect..."
                                />
                            </div>

                            <div className="col-span-1 lg:col-span-2">
                                <label htmlFor="pdfDownloadMessage" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    PDF Download Message
                                </label>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                    This text appears before the PDF download link when a PDF is successfully generated.
                                </p>
                                <textarea
                                    id="pdfDownloadMessage"
                                    className="w-full rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-transparent text-gray-900 dark:text-white px-4 py-3 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    rows="2"
                                    value={chatSettings.pdfDownloadMessage}
                                    onChange={(e) => handleChatSettingsChange('pdfDownloadMessage', e.target.value)}
                                    placeholder="You can view your full personalized CashFlow Plan PDF."
                                />
                            </div>

                            <div>
                                <label htmlFor="finalRedirectUrl" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Final Redirect URL
                                </label>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                    URL for the "Continue" button that appears after the final chat text. Users will be redirected here to complete their journey.
                                </p>
                                <input
                                    id="finalRedirectUrl"
                                    type="url"
                                    className="w-full rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-transparent text-gray-900 dark:text-white px-4 py-2.5 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    value={chatSettings.finalRedirectUrl}
                                    onChange={(e) => handleChatSettingsChange('finalRedirectUrl', e.target.value)}
                                    placeholder="https://"
                                />
                            </div>

                            <div className="flex items-center justify-between p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                                <div>
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">Enable PDF Download</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Allow users to download their personalized CashFlow Plan as a PDF.</p>
                                </div>
                                <ToggleSwitch
                                    enabled={chatSettings.enablePdfDownload}
                                    onChange={() => handleChatSettingsToggle('enablePdfDownload')}
                                />
                            </div>
                        </div>

                    </>
                )}
            </div>
        </div>
    );
};

export default ChatSettingsCard;

